/*
 * [TEAM-MGMT-2026-07-17-REBUILD] Assign sales/retention managers to their team leads.
 * Superadmin/admin: full control. Team leads: read-only view of their team.
 */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Users, UserCheck } from 'lucide-react';
import { AuthContext } from '../App';
import { useLanguage } from '../contexts/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const roleLabel = {
  sales_manager: 'Sales-менеджер',
  team_lead_sales: 'Тимлид Sales',
  retention_manager: 'Retention-менеджер',
  team_lead_retention: 'Тимлид Retention',
  manager: 'Менеджер',
};

const TeamManagement = () => {
  const { user } = useContext(AuthContext);
  const { tt } = useLanguage();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);

  const canEdit = user?.role === 'superadmin' || user?.role === 'admin';

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/users`);
      const filtered = (data || []).filter((u) => u.role && u.role !== 'client' && u.role !== 'CLIENT');
      setStaff(filtered);
    } catch (e) {
      toast.error(tt('Не удалось загрузить сотрудников'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const leadsSales = useMemo(() => staff.filter((s) => s.role === 'team_lead_sales'), [staff]);
  const leadsRetention = useMemo(() => staff.filter((s) => s.role === 'team_lead_retention'), [staff]);
  const salesManagers = useMemo(() => staff.filter((s) => s.role === 'sales_manager' || s.role === 'manager'), [staff]);
  const retentionManagers = useMemo(() => staff.filter((s) => s.role === 'retention_manager'), [staff]);

  const setTeamLead = async (userId, teamLeadId) => {
    try {
      await axios.patch(`${API}/users/${userId}/team-lead`, { team_lead_id: teamLeadId || null });
      toast.success(tt('Сохранено'));
      load();
    } catch (e) {
      toast.error(tt('Не удалось сохранить'));
    }
  };

  if (loading) {
    return <div className="p-8 text-center text-gray-500" data-testid="teams-loading">{tt('Загружаю…')}</div>;
  }

  return (
    <div className="space-y-6" data-testid="team-management-page">
      <TeamSection
        title={tt('Sales-команды')}
        icon={Users}
        leads={leadsSales}
        members={salesManagers}
        canEdit={canEdit}
        onAssign={setTeamLead}
        currentUser={user}
      />
      <TeamSection
        title={tt('Retention-команды')}
        icon={UserCheck}
        leads={leadsRetention}
        members={retentionManagers}
        canEdit={canEdit}
        onAssign={setTeamLead}
        currentUser={user}
      />
    </div>
  );
};


const TeamSection = ({ title, icon: Icon, leads, members, canEdit, onAssign, currentUser }) => {
  const { tt } = useLanguage();
  const unassigned = members.filter((m) => !m.team_lead_id);

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 overflow-hidden">
      <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2 bg-gray-50 dark:bg-gray-900">
        <Icon className="w-5 h-5 text-indigo-600" />
        <h3 className="font-semibold">{title}</h3>
        <span className="text-sm text-gray-500 ml-auto">{tt('Тимлидов:')} {leads.length} · {tt('Менеджеров:')} {members.length}</span>
      </div>

      {!leads.length ? (
        <div className="p-6 text-center text-gray-500 text-sm">{tt('Нет тимлидов этого типа. Создайте пользователя с ролью тимлида.')}</div>
      ) : (
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {leads.map((lead) => {
            const team = members.filter((m) => String(m.team_lead_id) === String(lead.id));
            return (
              <div key={lead.id} className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="font-medium">{lead.name}</div>
                    <div className="text-xs text-gray-500">{lead.email}</div>
                  </div>
                  <span className="text-xs px-2 py-1 rounded bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-200">
                    {tt('В команде:')} {team.length}
                  </span>
                </div>
                {team.length ? (
                  <ul className="ml-2 mt-2 space-y-1">
                    {team.map((m) => (
                      <li key={m.id} className="flex items-center justify-between text-sm py-1 px-2 rounded hover:bg-gray-50 dark:hover:bg-gray-700">
                        <span>{m.name} <span className="text-gray-400">· {m.email}</span></span>
                        {canEdit && (
                          <button
                            onClick={() => onAssign(m.id, null)}
                            className="text-xs text-red-600 hover:underline"
                            data-testid={`remove-member-${m.id}`}
                          >
                            {tt('удалить')}
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-xs text-gray-500 italic ml-2">{tt('Нет назначенных менеджеров')}</div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {canEdit && unassigned.length > 0 && leads.length > 0 && (
        <div className="border-t border-gray-200 dark:border-gray-700 p-4 bg-yellow-50 dark:bg-yellow-900/20">
          <div className="text-sm font-medium mb-2">{tt('Без команды:')} {unassigned.length}</div>
          <div className="space-y-2">
            {unassigned.map((m) => (
              <div key={m.id} className="flex items-center justify-between gap-2 text-sm bg-white dark:bg-gray-800 p-2 rounded">
                <span className="flex-1">{m.name} <span className="text-gray-400">· {m.email}</span></span>
                <select
                  className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-xs bg-white dark:bg-gray-800"
                  defaultValue=""
                  onChange={(e) => e.target.value && onAssign(m.id, e.target.value)}
                  data-testid={`assign-lead-${m.id}`}
                >
                  <option value="" disabled>{tt('Назначить тимлида…')}</option>
                  {leads.map((l) => (<option key={l.id} value={l.id}>{l.name}</option>))}
                </select>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamManagement;
